import React from 'react';
import { motion } from 'framer-motion';
import { Radio, ShieldCheck, Eye, BadgeCheck } from 'lucide-react';
const steps = [{
  num: '01',
  title: 'Tag',
  desc: "Every asset carries a small, tough tag that's built to last as long as the asset does."
}, {
  num: '02',
  title: 'Scan',
  desc: "It gets read automatically wherever it goes: the dock, the drop-off, our wash line. Nobody's scanning by hand."
}, {
  num: '03',
  title: 'Sync',
  desc: 'Everything we learn shows up on your dashboard as it happens.'
}];
const safetyFacts = [{
  icon: <ShieldCheck className="w-4 h-4" data-id="element-414" />,
  text: 'Safety certified for food and beverage applications'
}, {
  icon: <BadgeCheck className="w-4 h-4" data-id="element-415" />,
  text: 'Holds up to the wash line and rough handling'
}, {
  icon: <Eye className="w-4 h-4" data-id="element-416" />,
  text: 'Tucked away where your customers will never notice it'
}];
export const RFIDTracking: React.FC = () => {
  return <section id="technology" className="py-32 bg-sky-soft relative overflow-hidden" data-id="element-417">
      {/* Decorative pattern */}
      <div className="absolute inset-0 opacity-30 pointer-events-none" data-id="element-418">
        <svg width="100%" height="100%" preserveAspectRatio="none" data-id="element-419">
          <pattern id="circle-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse" data-id="element-420">
            <circle cx="30" cy="30" r="1" fill="var(--color-sky)" data-id="element-421" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#circle-pattern)" data-id="element-422" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-423">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-424">
          {/* Left: Scanner Visual */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="relative" data-id="element-425">
            <div className="relative aspect-square max-w-md mx-auto" data-id="element-426">
              {/* Background photo */}
              <div className="absolute inset-0 rounded-3xl overflow-hidden shadow-glossy-lg" data-id="element-427">
                <img src="https://images.unsplash.com/photo-1568644396922-5c3bfae12521?w=900&q=80&auto=format&fit=crop" alt="Returnable packaging being scanned on a conveyor" className="w-full h-full object-cover" data-id="element-428" />
                <div className="absolute inset-0 bg-primary/70" data-id="element-429" />
              </div>

              {/* Scanner overlay */}
              <div className="absolute inset-0 flex items-center justify-center p-8" data-id="element-430">
                <div className="relative w-full h-full" data-id="element-431">
                  {/* Mixed asset silhouettes — bottle + crate */}
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-44 h-44 flex items-end justify-center gap-2" data-id="element-432">
                    {/* Bottle */}
                    <svg viewBox="0 0 40 100" className="w-12 h-32" data-id="element-433">
                      <rect x="14" y="2" width="12" height="10" rx="1" fill="#e0a73d" data-id="element-434" />
                      <rect x="15" y="12" width="10" height="14" fill="#3aa856" opacity="0.95" data-id="element-435" />
                      <path d="M 15 26 Q 8 30 8 40 L 8 92 Q 8 96 12 96 L 28 96 Q 32 96 32 92 L 32 40 Q 32 30 25 26 Z" fill="#3aa856" data-id="element-436" />
                      <path d="M 11 35 Q 10 50 11 85" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" fill="none" data-id="element-437" />
                      <rect x="11" y="55" width="18" height="18" fill="rgba(255,255,255,0.12)" data-id="element-438" />
                    </svg>
                    {/* Mini crate */}
                    <svg viewBox="0 0 80 100" className="w-20 h-24" data-id="element-439">
                      <rect x="4" y="40" width="72" height="50" fill="#c97a4a" stroke="rgba(0,0,0,0.2)" strokeWidth="1" rx="2" data-id="element-440" />
                      <rect x="4" y="40" width="72" height="6" fill="rgba(0,0,0,0.15)" data-id="element-441" />
                      {[8, 22, 36, 50, 64].map((x, i) => <g key={i} data-id="element-442">
                          <rect x={x} y="22" width="8" height="22" fill="#3aa856" rx="0.5" data-id="element-443" />
                          <rect x={x + 2} y="16" width="4" height="7" fill="#3aa856" data-id="element-444" />
                          <rect x={x + 2} y="13" width="4" height="4" fill="rgba(0,0,0,0.5)" data-id="element-445" />
                        </g>)}
                      <rect x="22" y="62" width="36" height="14" fill="rgba(255,255,255,0.85)" rx="1" data-id="element-446" />
                      <rect x="26" y="66" width="28" height="2" fill="#c97a4a" data-id="element-447" />
                    </svg>
                  </div>

                  {/* Animated scan rings */}
                  {[0, 1, 2].map(i => <motion.div key={i} className="absolute left-1/2 top-[55%] -translate-x-1/2 -translate-y-1/2 border-2 border-leaf rounded-full" initial={{
                  width: 30,
                  height: 30,
                  opacity: 0
                }} animate={{
                  width: [30, 260],
                  height: [30, 260],
                  opacity: [0.9, 0]
                }} transition={{
                  duration: 2.5,
                  delay: i * 0.8,
                  repeat: Infinity,
                  ease: 'easeOut'
                }} data-id="element-448" />)}

                  {/* Scan line */}
                  <motion.div className="absolute left-0 right-0 h-0.5 bg-leaf shadow-leaf-glow" animate={{
                  top: ['10%', '90%', '10%']
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-449" />

                  {/* Corner brackets */}
                  {['top-4 left-4 border-t-2 border-l-2', 'top-4 right-4 border-t-2 border-r-2', 'bottom-4 left-4 border-b-2 border-l-2', 'bottom-4 right-4 border-b-2 border-r-2'].map((pos, i) => <div key={i} className={`absolute w-8 h-8 ${pos} border-leaf`} data-id="element-450" />)}

                  {/* Data badges floating around */}
                  <motion.div className="absolute top-6 right-2 bg-white/95 backdrop-blur rounded-md px-2.5 py-1 shadow-glossy" initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.6
                }} data-id="element-451">
                    <div className="font-mono text-[9px] text-leaf uppercase tracking-wider" data-id="element-452">
                      Bottle OK
                    </div>
                  </motion.div>
                  <motion.div className="absolute bottom-16 left-2 bg-white/95 backdrop-blur rounded-md px-2.5 py-1 shadow-glossy" initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.9
                }} data-id="element-453">
                    <div className="font-mono text-[9px] text-sky uppercase tracking-wider" data-id="element-454">
                      Crate OK
                    </div>
                  </motion.div>

                  {/* Bottom live data badge */}
                  <motion.div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur px-4 py-2 rounded-full shadow-glossy" initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.4
                }} data-id="element-455">
                    <div className="font-mono text-xs text-primary flex items-center gap-2" data-id="element-456">
                      <span className="w-2 h-2 rounded-full bg-leaf animate-pulse" data-id="element-457" />
                      2 ASSETS LOGGED
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Trust badges below the visual */}
            <div className="mt-6 flex items-center justify-center gap-3 flex-wrap" data-id="element-458">
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white shadow-glossy border border-leaf/20" data-id="element-459">
                <ShieldCheck className="w-3.5 h-3.5 text-leaf" data-id="element-460" />
                <span className="font-mono text-[10px] uppercase tracking-wider text-primary" data-id="element-461">
                  Safety Certified
                </span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white shadow-glossy border border-sky/20" data-id="element-462">
                <BadgeCheck className="w-3.5 h-3.5 text-sky" data-id="element-463" />
                <span className="font-mono text-[10px] uppercase tracking-wider text-primary" data-id="element-464">
                  Industry Compliant
                </span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white shadow-glossy border border-accent/30" data-id="element-465">
                <Eye className="w-3.5 h-3.5 text-accent" data-id="element-466" />
                <span className="font-mono text-[10px] uppercase tracking-wider text-primary" data-id="element-467">
                  Invisible to Consumers
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right: Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-468">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky text-white text-sm font-mono mb-6 shadow-sky-glow" data-id="element-469">
              <Radio className="w-4 h-4" data-id="element-470" /> Smart Tracking
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-primary mb-6" data-id="element-471">
              Visibility on every
              <br data-id="element-472" />
              asset, every cycle.
            </h2>
            <p className="text-lg text-ink/70 mb-8 leading-relaxed" data-id="element-473">
              Every bottle, crate, and pallet carries our smart tracking. As it
              gets filled, shipped, returned, and cleaned, it logs itself along
              the way. You see exactly where things are, and your team doesn't
              have to do anything differently.
            </p>

            {/* Safety callout */}
            <div className="bg-white rounded-2xl p-6 mb-8 shadow-glossy gloss-card relative border border-leaf/15" data-id="element-474">
              <div className="relative" data-id="element-475">
                <div className="flex items-center gap-2 mb-4" data-id="element-476">
                  <ShieldCheck className="w-5 h-5 text-leaf" data-id="element-477" />
                  <h4 className="font-medium text-primary" data-id="element-478">
                    Built for the real world
                  </h4>
                </div>
                <ul className="space-y-2.5" data-id="element-479">
                  {safetyFacts.map((fact, i) => <li key={i} className="flex items-start gap-3 text-sm text-ink/75" data-id="element-480">
                      <span className="text-leaf mt-0.5 shrink-0" data-id="element-481">
                        {fact.icon}
                      </span>
                      <span data-id="element-482">{fact.text}</span>
                    </li>)}
                </ul>
              </div>
            </div>

            <div className="space-y-3" data-id="element-483">
              {steps.map((step, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-glossy gloss-card relative" data-id="element-484">
                  <div className="font-mono text-sm text-sky font-medium relative" data-id="element-485">
                    {step.num}
                  </div>
                  <div className="relative" data-id="element-486">
                    <h4 className="font-medium text-primary mb-1" data-id="element-487">
                      {step.title}
                    </h4>
                    <p className="text-sm text-ink/70" data-id="element-488">{step.desc}</p>
                  </div>
                </motion.div>)}
            </div>

            <div className="mt-8 flex items-center gap-6 pt-6 border-t border-primary/10" data-id="element-489">
              <div data-id="element-490">
                <div className="font-mono text-2xl text-sky" data-id="element-491">99.7%</div>
                <div className="text-xs text-muted" data-id="element-492">Scan accuracy</div>
              </div>
              <div className="w-px h-10 bg-primary/10" data-id="element-493" />
              <div data-id="element-494">
                <div className="font-mono text-2xl text-leaf" data-id="element-495">Real-time</div>
                <div className="text-xs text-muted" data-id="element-496">Lifecycle data</div>
              </div>
              <div className="w-px h-10 bg-primary/10" data-id="element-497" />
              <div data-id="element-498">
                <div className="font-mono text-2xl text-accent" data-id="element-499">All RPM</div>
                <div className="text-xs text-muted" data-id="element-500">Asset types</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
};